import { useLayoutEffect, useState } from 'react'

const isClipped = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth ||
  element.scrollHeight > element.clientHeight

/**
 * Reports whether an element is showing less than it holds, by asking the
 * browser what it laid out rather than predicting it from font metrics.
 *
 * The ref belongs on the element that clips. Measuring anything further out
 * reports nothing hidden, because the clipping element has already cut the
 * content away before its ancestors ever see it.
 */
export const useIsContentClipped = <
  TElement extends HTMLElement = HTMLElement,
>() => {
  const [element, setElement] = useState<TElement | null>(null)
  const [isContentClipped, setIsContentClipped] = useState(false)

  useLayoutEffect(() => {
    if (!element) {
      return
    }

    const measure = () => setIsContentClipped(isClipped(element))

    // Measured before the browser paints, so content that does not fit is
    // never shown to the reader on its way to being replaced.
    measure()

    // What fits changes for two reasons, and neither is a render of this
    // component: the space can change under content that stayed the same, when
    // a column or the window is resized, and the content can change within
    // space that stayed the same, when a different expression is shown.
    const resizeObserver = new ResizeObserver(measure)
    const mutationObserver = new MutationObserver(measure)

    resizeObserver.observe(element)
    mutationObserver.observe(element, {
      subtree: true,
      childList: true,
      characterData: true,
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [element])

  return { contentRef: setElement, isContentClipped }
}
